import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef } from 'react';

import { Instance } from '@popperjs/core';
import { Camera } from '@yandex-360/iconostasis/dist/icons/Camera';
import { CameraOff } from '@yandex-360/iconostasis/dist/icons/CameraOff';
import { MicroOff } from '@yandex-360/iconostasis/dist/icons/MicroOff';
import { MicroOn } from '@yandex-360/iconostasis/dist/icons/MicroOn';
import { Popover2Props } from '@yandex-360/orb-components/dist/components/Popover2/types';
import { PeerType } from '@yandex-360/telemost-sdk/models';
import { Event as CustomEvent } from '@yandex-int/messenger.utils';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import {
    selectHasCameraMutePermission,
    selectHasDesktopMutePermission,
    selectHasMicrophoneMutePermission,
    selectHasRecordingMutePermission,
} from '../../../pages/call/features/permissions';
import {
    getParticipantByIds,
    isSelfModerator as isSelfModeratorSelector,
    isSelfOwner as isSelfOwnerSelector,
    selectCanMuteAllPeerReactions,
    selectCanMutePeerReaction,
    selectHasNonSelfParticipantRecordingActive,
    selectHasNonSelfParticipantSuppressibleRaisedHand,
    selectIsPermissionsExtendedEnabled,
    selectIsReactionsAvailable,
} from '../../../pages/call/selectors';
import { ParticipantID, ParticipantState } from '../../../pages/call/types';
import { SELF } from '../../../shared/constants';
import { getIsKickEnabled } from '../../features/config/selectors';
import {
    makeUserAnMember,
    makeUserAnModerator,
    muteAllPeerAudio,
    muteAllPeerReactions,
    muteAllPeerVideo,
    mutePeerAudio,
    mutePeerReaction,
    mutePeerScreenSharing,
    mutePeerVideo,
} from '../../features/moderation/slice';
import { isB2bUserSelector } from '../../features/user/selectors';
import { i18n } from '../../helpers/i18n';
import { countMetrika } from '../../helpers/metrika';
import MoreButtonPopover from '../more-button-popover';
import { MorePopupOption } from '../more-button-popover/option';
import css from './styles.css';

interface ModerationPopupProps extends Popover2Props {
    participantId: string;
    className?: string;
    onClose(event?: Event): void;
    onKickConfirm?(participantId: ParticipantID): void;
    onStopRecordingConfirm?(participantId: ParticipantID | 'all'): void;
    hideOnResize?: boolean;
    isMobile?: boolean;
    onUpdate?: CustomEvent;
    isRecordingActive?: boolean;
}

// поиск хотябы одного пира с включенным аудио/видео
const areAllParticipantsMuted = (participants: Partial<Record<string, ParticipantState>>, type: 'audio' | 'video') =>
    !Object.values(participants).some((item) => item && item.id !== SELF && item[type] && !item[type]?.muted);

const hasActiveDesktopSharing = (participant?: ParticipantState) => participant?.desktop && !participant.desktop.muted;

const ModerationPopup: FunctionComponent<ModerationPopupProps> = ({
    participantId,
    className,
    placement = 'top',
    onKickConfirm,
    onStopRecordingConfirm,
    isMobile,
    onUpdate,
    isRecordingActive = false,
    ...props
}) => {
    const dispatch = useDispatch();
    const participantsByIds = useSelector(getParticipantByIds);
    const hasCameraMutePermission = useSelector(selectHasCameraMutePermission);
    const hasMicrophoneMutePermission = useSelector(selectHasMicrophoneMutePermission);
    const hasRecordingMutePermission = useSelector(selectHasRecordingMutePermission);
    const idUrl = useSelector((state) => state.config.yaServices.id);
    const participant = participantsByIds[participantId];
    const isSelfModerator = useSelector(isSelfModeratorSelector);
    const isSelfOwner = useSelector(isSelfOwnerSelector);
    const mutedVideo = !participant?.video || participant.video.muted;
    const mutedAudio = !participant?.audio || participant.audio.muted;
    const areAllParticipantsAudioMuted = areAllParticipantsMuted(participantsByIds, 'audio');
    const areAllParticipantsVideoMuted = areAllParticipantsMuted(participantsByIds, 'video');
    const kickEnabled = useSelector(getIsKickEnabled);
    const isB2bUser = useSelector(isB2bUserSelector);
    const reactionType = participant?.reaction?.reactionType;
    const isReactionSuppressible = participant?.reaction?.suppressible;

    const isReactionsAvailable = useSelector(selectIsReactionsAvailable);
    const canMutePeerReaction = useSelector(selectCanMutePeerReaction);
    const canMuteAllPeerReactions = useSelector(selectCanMuteAllPeerReactions);
    const hasSuppressibleRaisedHand = useSelector(selectHasNonSelfParticipantSuppressibleRaisedHand);
    const hasNonSelfParticipantRecordingActive = useSelector(selectHasNonSelfParticipantRecordingActive);

    const childParticipant = useMemo(
        () => Object.values(participantsByIds).find((item) => item?.refParticipantId === participant?.id),
        [participantsByIds, participant?.id]
    );

    // Расширенные права участников
    const isPermissionsExtendedEnabled = useSelector(selectIsPermissionsExtendedEnabled);
    const hasDesktopMutePermission = useSelector(selectHasDesktopMutePermission);

    const handleKick = useCallback(() => {
        countMetrika('meeting_screen', 'moderation', 'kick', 'handle_kick_button');
        onKickConfirm?.(participant?.id || participantId);
    }, [onKickConfirm, participant?.id, participantId]);

    const handleStopRecording = useCallback(() => {
        if (participantId === SELF) {
            return onStopRecordingConfirm?.('all');
        }
        onStopRecordingConfirm?.(participant?.id || participantId);
    }, [onStopRecordingConfirm, participant?.id, participantId]);

    const onOptionClick = useCallback(
        (action, metrikaKey = null) => {
            if (metrikaKey) {
                countMetrika('meeting_screen', 'participants_list', metrikaKey);
            }
            dispatch(action(participant?.id));
        },
        [participant?.id, dispatch]
    );

    const shouldRenderMuteScreenSharing =
        (hasActiveDesktopSharing(participant) || hasActiveDesktopSharing(childParticipant)) &&
        (!isPermissionsExtendedEnabled || hasDesktopMutePermission);

    const items = useMemo(() => {
        const result = [];

        const CameraIcon = mutedVideo ? CameraOff : Camera;
        const MicroIcon = mutedAudio ? MicroOff : MicroOn;
        const AllCameraIcon = areAllParticipantsVideoMuted ? CameraOff : Camera;
        const AllMicroIcon = areAllParticipantsAudioMuted ? MicroOff : MicroOn;

        if (participantId === SELF) {
            result.push(
                <MorePopupOption
                    key="action-change-name-and-avatar"
                    className={css.Option}
                    onClick={() => countMetrika('meeting_screen', 'participants_list', 'change_photo')}
                    link={`${idUrl}/personal?dialog=personal-data&origin=b2b360&retpath=${idUrl}`}
                    iconName="user"
                    title={i18n('moderation', isB2bUser ? 'action-change-avatar' : 'action-change-name-and-avatar')}
                    content={i18n('moderation', isB2bUser ? 'action-change-avatar' : 'action-change-name-and-avatar')}
                />
            );

            if (isReactionsAvailable && canMuteAllPeerReactions) {
                result.push(
                    <MorePopupOption
                        key="action-mute-all-reactions-hand"
                        className={css.Option}
                        onClick={() => onOptionClick(muteAllPeerReactions)}
                        iconName="handUp"
                        title={i18n('moderation', 'action-mute-all-reactions-hand')}
                        content={i18n('moderation', 'action-mute-all-reactions-hand')}
                        disabled={!hasSuppressibleRaisedHand}
                    />
                );
            }
            const muteAllAudioOption = (
                <MorePopupOption
                    key="action-mute-all-audio"
                    className={css.Option}
                    onClick={() => onOptionClick(muteAllPeerAudio, 'mute_all')}
                    icon={<AllMicroIcon variant="solid" size="md" />}
                    title={i18n('moderation', 'action-mute-all-audio')}
                    content={i18n('moderation', 'action-mute-all-audio')}
                    disabled={areAllParticipantsAudioMuted}
                />
            );
            const muteAllVideoOption = (
                <MorePopupOption
                    key="action-mute-all-video"
                    className={css.Option}
                    onClick={() => onOptionClick(muteAllPeerVideo, 'video_off_all')}
                    icon={<AllCameraIcon variant="solid" size="md" />}
                    title={i18n('moderation', 'action-mute-all-video')}
                    content={i18n('moderation', 'action-mute-all-video')}
                    disabled={areAllParticipantsVideoMuted}
                />
            );
            if (isSelfModerator && !isPermissionsExtendedEnabled) {
                result.push(muteAllAudioOption, muteAllVideoOption);
            } else if (isPermissionsExtendedEnabled) {
                if (hasCameraMutePermission) {
                    result.push(muteAllVideoOption);
                }
                if (hasMicrophoneMutePermission) {
                    result.push(muteAllAudioOption);
                }
            }

            if (isPermissionsExtendedEnabled && hasRecordingMutePermission && hasNonSelfParticipantRecordingActive) {
                result.push(
                    <MorePopupOption
                        key="action"
                        className={css.Option}
                        onClick={handleStopRecording}
                        iconName="record"
                        title={i18n('moderation', 'action-stop-all-local-recordings')}
                        content={i18n('moderation', 'action-stop-all-local-recordings')}
                    />
                );
            }

            return result;
        }

        if (participant?.peerType === PeerType.INCOMING_PHONE_CALL) {
            if (isMobile && (!isPermissionsExtendedEnabled || hasMicrophoneMutePermission)) {
                result.push(
                    <MorePopupOption
                        key="action-mute-mic"
                        className={css.Option}
                        onClick={() => onOptionClick(mutePeerAudio, 'mute_participant')}
                        icon={<MicroIcon variant="solid" size="md" />}
                        title={i18n('toolbar', 'turn-off-mic')}
                        content={i18n('toolbar', 'turn-off-mic')}
                        disabled={mutedAudio}
                    />
                );
            }

            if (kickEnabled) {
                result.push(
                    <MorePopupOption
                        key="action-kick-user"
                        className={css.Option}
                        onClick={handleKick}
                        iconName="closePromo"
                        title={i18n('moderation', 'action-kick-user')}
                        content={i18n('moderation', 'action-kick-user')}
                    />
                );
            }

            return result;
        }

        if (participant?.uid) {
            if (
                isPermissionsExtendedEnabled &&
                isSelfOwner &&
                participant &&
                participant.isModerator &&
                !participant.isOwner
            ) {
                result.push(
                    <MorePopupOption
                        key="action-unmake-an-co-host"
                        className={css.Option}
                        onClick={() => onOptionClick(makeUserAnMember, 'make_member')}
                        iconName="crown"
                        title={i18n('moderation', 'action-unmake-an-co-host')}
                        content={i18n('moderation', 'action-unmake-an-co-host')}
                    />
                );
            } else if (isSelfModerator) {
                result.push(
                    <MorePopupOption
                        key="action-make-an-co-host"
                        className={css.Option}
                        onClick={() => onOptionClick(makeUserAnModerator, 'make_admin')}
                        iconName="crown"
                        title={i18n('moderation', 'action-make-an-co-host')}
                        content={i18n('moderation', 'action-make-an-co-host')}
                        disabled={participant?.isModerator}
                    />
                );
            }
        }

        if (isReactionsAvailable && canMutePeerReaction) {
            result.push(
                <MorePopupOption
                    key="action-mute-reaction-hand"
                    className={css.Option}
                    onClick={() => onOptionClick(mutePeerReaction)}
                    iconName="handUp"
                    title={i18n('moderation', 'action-mute-reaction-hand')}
                    content={i18n('moderation', 'action-mute-reaction-hand')}
                    disabled={reactionType !== 'hand' || !isReactionSuppressible}
                />
            );
        }

        if (isMobile && (!isPermissionsExtendedEnabled || hasMicrophoneMutePermission)) {
            result.push(
                <MorePopupOption
                    key="action-mute-mic"
                    className={css.Option}
                    onClick={() => onOptionClick(mutePeerAudio, 'mute_participant')}
                    icon={<MicroIcon variant="solid" size="md" />}
                    title={i18n('toolbar', 'turn-off-mic')}
                    content={i18n('toolbar', 'turn-off-mic')}
                    disabled={mutedAudio}
                />
            );
        }

        if (!isPermissionsExtendedEnabled || hasCameraMutePermission) {
            result.push(
                <MorePopupOption
                    key="action-mute-video"
                    className={css.Option}
                    onClick={() => onOptionClick(mutePeerVideo, 'video_off__participant')}
                    icon={<CameraIcon variant="solid" size="md" />}
                    title={i18n('moderation', 'action-mute-video')}
                    content={i18n('moderation', 'action-mute-video')}
                    disabled={mutedVideo}
                />
            );
        }

        if (shouldRenderMuteScreenSharing) {
            result.push(
                <MorePopupOption
                    key="action-mute-screensharing"
                    className={css.Option}
                    onClick={() => onOptionClick(mutePeerScreenSharing)}
                    iconName="shareScreen"
                    title={i18n('moderation', 'action-mute-screensharing')}
                    content={i18n('moderation', 'action-mute-screensharing')}
                />
            );
        }

        if (isPermissionsExtendedEnabled && hasRecordingMutePermission && isRecordingActive) {
            result.push(
                <MorePopupOption
                    key="action"
                    className={css.Option}
                    onClick={handleStopRecording}
                    iconName="record"
                    title={i18n('moderation', 'action-stop-local-recording')}
                    content={i18n('moderation', 'action-stop-local-recording')}
                />
            );
        }

        if (kickEnabled && isSelfModerator) {
            result.push(
                <MorePopupOption
                    key="action-kick-user"
                    className={css.Option}
                    onClick={handleKick}
                    iconName="closePromo"
                    title={i18n('moderation', 'action-kick-user')}
                    content={i18n('moderation', 'action-kick-user')}
                />
            );
        }

        return result;
    }, [
        mutedVideo,
        areAllParticipantsVideoMuted,
        areAllParticipantsAudioMuted,
        participantId,
        kickEnabled,
        participant,
        isReactionsAvailable,
        canMutePeerReaction,
        isMobile,
        shouldRenderMuteScreenSharing,
        isPermissionsExtendedEnabled,
        hasRecordingMutePermission,
        isRecordingActive,
        isSelfModerator,
        idUrl,
        isB2bUser,
        canMuteAllPeerReactions,
        hasNonSelfParticipantRecordingActive,
        hasSuppressibleRaisedHand,
        onOptionClick,
        hasCameraMutePermission,
        hasMicrophoneMutePermission,
        handleStopRecording,
        handleKick,
        isSelfOwner,
        reactionType,
        isReactionSuppressible,
        mutedAudio,
    ]);

    const popperInstance = useRef<Instance>();

    useEffect(() => {
        if (!popperInstance || !onUpdate) {
            return;
        }

        const handleUpdate = () => {
            popperInstance.current?.update();
        };

        onUpdate.addListener(handleUpdate);

        return () => {
            onUpdate.removeListener(handleUpdate);
        };
    }, [onUpdate, popperInstance]);

    return (
        <MoreButtonPopover
            {...props}
            className={cn(className, css.ModerationPopup)}
            items={items}
            placement={placement}
            isOpen
        />
    );
};

export default ModerationPopup;
